import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Send, ArrowUpRight, ShieldCheck, AlertTriangle, Users, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatMoney } from "../lib/api";

const Dashboard = () => {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [rates, setRates] = useState([]);
  const [corridors, setCorridors] = useState([]);

  useEffect(() => {
    api.get("/transfers").then(r => setTransfers(r.data)).catch(() => {});
    api.get("/recipients").then(r => setRecipients(r.data)).catch(() => {});
    api.get("/fx/rates").then(r => setRates(r.data.rates)).catch(() => {});
    api.get("/stats/corridors").then(r => setCorridors(r.data)).catch(() => {});
  }, []);

  const sent = transfers.filter(t => t.status === "delivered" || t.status === "paid")
    .reduce((sum, t) => sum + t.source_amount, 0);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid="dashboard-root">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-1.5">Dashboard</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Hi, {user?.full_name?.split(" ")[0]}.</h1>
          <p className="text-[var(--ra-muted)] mt-1">Ready to send some love home?</p>
        </div>
        <Link to="/send">
          <Button size="lg" className="btn-primary rounded-md h-12 px-7" data-testid="dashboard-quick-send-btn">
            <Send size={16} className="mr-2" /> Send money
          </Button>
        </Link>
      </div>

      {user?.kyc_status !== "approved" && (
        <Link to="/kyc">
          <div className="ra-card p-4 mb-6 border-[#E9CFA1] bg-[#FDF6E7] flex items-center gap-3" data-testid="kyc-banner">
            <AlertTriangle className="text-[var(--ra-warning)]" size={20} />
            <div className="flex-1">
              <div className="font-semibold text-sm">Verify your identity to start sending</div>
              <div className="text-xs text-[var(--ra-muted)]">Quick 2-minute KYC check is required.</div>
            </div>
            <ArrowUpRight size={18} className="text-[var(--ra-muted)]" />
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <Card title="Total sent" value={formatMoney(sent, "USD")} icon={Send} testid="stat-total-sent" />
        <Card title="Recipients" value={recipients.length} icon={Users} testid="stat-recipients" />
        <Card title="Transfers" value={transfers.length} icon={Clock} testid="stat-transfers" />
        <Card title="KYC status" value={user?.kyc_status === "approved" ? "Verified" : "Pending"} icon={ShieldCheck} accent={user?.kyc_status === "approved"} testid="stat-kyc" />

        {/* Corridor heatmap */}
        {corridors.length > 0 && (
          <div className="ra-card p-6 md:col-span-3 lg:col-span-4 fade-up" data-testid="corridor-heatmap">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-muted)] font-semibold">Top corridors</div>
                <h3 className="font-heading text-lg font-semibold mt-1">Where ClearPurse money flows</h3>
              </div>
            </div>
            <div className="space-y-3">
              {corridors.map((c, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`corridor-${c.source_currency}-${c.destination_country}`}>
                  <div className="text-2xl shrink-0">{c.source_flag}<span className="text-[var(--ra-muted)] mx-1 text-base">→</span>{c.destination_flag}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{c.source_currency} → {c.destination_country}</span>
                      <span className="text-[var(--ra-muted)]">{c.share}% · {c.count} transfer{c.count > 1 ? "s" : ""}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--ra-border)] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[var(--ra-primary)] to-[var(--ra-accent)] transition-all" style={{ width: `${c.share}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick FX */}
        <div className="ra-card p-6 md:col-span-2 lg:col-span-3 fade-up fade-up-2" data-testid="dash-fx-widget">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-muted)] font-semibold">Live FX rates</div>
              <h3 className="font-heading text-xl font-semibold mt-1">1 USD buys you</h3>
            </div>
            <Link to="/rates"><Button variant="ghost" size="sm" className="text-[var(--ra-primary)]" data-testid="dash-fx-view-all">View all</Button></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {rates.slice(0, 5).map((r) => (
              <div key={r.country_code} className="border border-[var(--ra-border)] rounded-lg p-3" data-testid={`dash-rate-${r.country_code}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{r.flag}</span>
                  <span className="text-xs text-[var(--ra-muted)] uppercase tracking-wider">{r.currency}</span>
                </div>
                <div className="font-heading font-bold">{r.rate.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent transfers */}
        <div className="ra-card p-6 md:col-span-3 lg:col-span-3 fade-up fade-up-3" data-testid="dash-recent-transfers">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">Recent transfers</h3>
            <Link to="/history"><Button variant="ghost" size="sm" className="text-[var(--ra-primary)]" data-testid="dash-history-link">All history</Button></Link>
          </div>
          {transfers.length === 0 ? (
            <Empty title="No transfers yet" body="Send your first transfer to see receipts here." cta="/send" ctaLabel="Send money" />
          ) : (
            <div className="divide-y divide-[var(--ra-border)]">
              {transfers.slice(0, 5).map((t) => (
                <div key={t.id} className="py-3 flex items-center gap-3" data-testid={`dash-transfer-${t.reference}`}>
                  <div className="w-9 h-9 rounded-lg bg-[var(--ra-primary)]/8 flex items-center justify-center text-[var(--ra-primary)]"><Send size={16} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{t.recipient_snapshot.full_name}</div>
                    <div className="text-xs text-[var(--ra-muted)]">{t.reference} · {new Date(t.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{formatMoney(t.source_amount, t.source_currency)}</div>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${t.status === "delivered" ? "border-[var(--ra-success)] text-[var(--ra-success)]" : "border-[var(--ra-warning)] text-[var(--ra-warning)]"}`}>{t.status.replace("_", " ")}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recipients */}
        <div className="ra-card p-6 md:col-span-3 lg:col-span-1 fade-up fade-up-4" data-testid="dash-recipients-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">Beneficiaries</h3>
            <Link to="/recipients"><Button variant="ghost" size="sm" className="text-[var(--ra-primary)]" data-testid="dash-recipients-link">Manage</Button></Link>
          </div>
          {recipients.length === 0 ? (
            <Empty title="No recipients" body="Add someone to start sending." cta="/recipients" ctaLabel="Add recipient" />
          ) : (
            <div className="space-y-3">
              {recipients.slice(0, 4).map((r) => (
                <div key={r.id} className="flex items-center gap-3" data-testid={`dash-recipient-${r.id}`}>
                  <div className="w-9 h-9 rounded-full bg-[var(--ra-accent)]/15 text-[var(--ra-accent)] font-semibold flex items-center justify-center">{r.full_name[0]?.toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.full_name}</div>
                    <div className="text-xs text-[var(--ra-muted)]">{r.country_code} · {r.delivery_method.replace("_", " ")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Card = ({ title, value, icon: Icon, accent, testid }) => (
  <div className="ra-card p-5 fade-up" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-muted)] font-semibold">{title}</div>
        <div className={`font-heading text-2xl font-bold mt-2 ${accent ? "text-[var(--ra-success)]" : ""}`}>{value}</div>
      </div>
      <div className="w-9 h-9 rounded-lg bg-[var(--ra-primary)]/8 flex items-center justify-center text-[var(--ra-primary)]"><Icon size={16} /></div>
    </div>
  </div>
);

const Empty = ({ title, body, cta, ctaLabel }) => (
  <div className="py-8 text-center">
    <div className="text-sm font-medium">{title}</div>
    <div className="text-xs text-[var(--ra-muted)] mt-1 mb-4">{body}</div>
    <Link to={cta}><Button size="sm" className="btn-primary rounded-md">{ctaLabel}</Button></Link>
  </div>
);

export default Dashboard;
